import { PAYLOAD_URL } from "~/constants/all";
import { updateCollectionItemById } from "~/lib/payload/collection";
import rest from "~/lib/payload/rest";
import type { ApiResponse, CaughtError, PayloadApiResponse } from "~/types";
import type { Invitation } from "~/types/collections";

/**
 * Interface for invitation operation response
 */
interface InvitationOperationResponse {
  doc?: Invitation;
  message?: string;
}

/**
 * Base function for handling invitation actions
 * @param options - The options for the invitation action
 * @returns A promise that resolves to the API response
 */
const invitationBaseApi = async ({
  id,
  type,
}: {
  id: string;
  type: string;
}): Promise<ApiResponse<Invitation>> => {
  if (!id) {
    return {
      success: false,
      message: "Invitation ID is required",
    };
  }

  if (!type) {
    return {
      success: false,
      message: "Type is required",
    };
  }

  try {
    const res = await rest<PayloadApiResponse<InvitationOperationResponse>>(
      `${PAYLOAD_URL}/api/invitations/${id}/${type}`,
      {},
      {
        method: "POST",
      }
    );

    if (res.errors) {
      return {
        success: false,
        message: res.errors[0]?.data?.[0]?.message || res.errors[0].message,
      };
    }

    if (!res.doc) {
      return {
        success: false,
        message: res.message || "Failed to process invitation",
      };
    }

    return {
      success: true,
      data: res.doc,
      message: res.message,
    };
  } catch (e) {
    const error = e as CaughtError;
    return {
      success: false,
      message: error.message,
    };
  }
};

/**
 * Accepts an invitation
 * @param id - The ID of the invitation
 * @returns A promise that resolves to the API response
 */
export const acceptInvitation = async (
  id: string
): Promise<ApiResponse<Invitation>> =>
  updateCollectionItemById({
    collection: "invitations",
    id,
    data: { status: "accepted" },
  });

/**
 * Declines an invitation
 * @param id - The ID of the invitation
 * @returns A promise that resolves to the API response
 */
export const declineInvitation = async (
  id: string
): Promise<ApiResponse<Invitation>> =>
  updateCollectionItemById({
    collection: "invitations",
    id,
    data: { status: "declined" },
  });

/**
 * Resends an invitation
 * @param id - The ID of the invitation
 * @returns A promise that resolves to the API response
 */
export const resendInvitation = async (
  id: string
): Promise<ApiResponse<Invitation>> =>
  invitationBaseApi({ id, type: "resend" });

/**
 * Set a single inviation when not logged in
 * @param token - The token of the invitation
 * @returns A promise that resolves to the API response
 * */
export const getSingleInvitation = async ({
  invitation,
  token,
}: {
  invitation: string;
  token: string;
}): Promise<ApiResponse<Invitation>> => {
  try {
    const res = await rest<PayloadApiResponse<Invitation>>(
      `${PAYLOAD_URL}/api/invitations/${invitation}?token=${token}`,
      {},
      {
        method: "GET",
      }
    );

    if (res.errors) {
      return {
        success: false,
        message: res.errors[0]?.data?.[0]?.message || res.errors[0].message,
      };
    }

    return {
      success: true,
      data: res,
    };
  } catch (e) {
    const error = e as CaughtError;
    return {
      success: false,
      message: error.message,
    };
  }
};
